import { useState, useEffect, useRef, Fragment } from 'react';
import {
  TextField,
  InputAdornment,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  makeStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { documentationStructure } from './documentationData';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    position: 'relative',
    width: 300,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1), 0 2px 8px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  searchResults: {
    maxHeight: '400px',
    overflowY: 'auto',
    marginTop: '4px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#ffffff',
    zIndex: 1300,
  },
  resultItem: {
    cursor: 'pointer',
    padding: '12px 16px',
    borderBottom: '1px solid #f5f5f5',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  resultBreadcrumb: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
    fontWeight: 500,
  },
  resultTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1976d2',
    marginBottom: '4px',
  },
  resultSnippet: {
    fontSize: '13px',
    color: '#424242',
    lineHeight: 1.4,
  },
  highlight: {
    backgroundColor: '#fff3cd',
    padding: '1px 2px',
    borderRadius: '2px',
    fontWeight: 600,
  },
  categoryHeader: {
    padding: '8px 16px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '12px',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
  },
  noResults: {
    padding: '20px',
    textAlign: 'center',
    color: '#666',
    fontSize: '14px',
  },
  loadingContainer: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const GlobalSearch = ({ onNavigate }) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchIndex, setSearchIndex] = useState([]);
  const searchRef = useRef();
  const anchorRef = useRef();

  // Build search index on component mount
  useEffect(() => {
    buildSearchIndex();
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        searchRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Build searchable index from documentation data
  const buildSearchIndex = () => {
    const index = [];

    const processItem = (item, breadcrumb = []) => {
      const currentBreadcrumb = [...breadcrumb, item.title];

      if (item.content) {
        // Process markdown content
        if (item.content.markdown) {
          const textContent = item.content.markdown
            .replace(/#+\s/g, '') // Remove markdown headers
            .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
            .replace(/\*(.*?)\*/g, '$1') // Remove italic
            .replace(/`(.*?)`/g, '$1') // Remove code
            .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Remove links
            .replace(/<[^>]*>/g, '') // Remove HTML tags
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&') // Decode HTML entities
            .replace(/\|/g, '') // Remove table separators
            .replace(/[-]{2,}/g, '') // Remove table formatting
            .replace(/\n+/g, ' ') // Replace newlines with spaces
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();

          // Split into sentences for better snippet extraction
          const sentences = textContent.split(/[.!?]+/).filter(s => {
            const sentence = s.trim();
            // Filter out very short sentences, table headers, and markdown artifacts
            return sentence.length > 30 &&
                   !sentence.match(/^\s*\|/) && // Not table rows
                   !sentence.match(/^[-\s]+$/) && // Not separators
                   !sentence.match(/^Required\s*$/) && // Not just "Required"
                   sentence.split(' ').length > 3; // Has multiple words
          });

          sentences.forEach(sentence => {
            const cleanSentence = sentence.trim()
              .replace(/^\s*[|\-\s]+/, '') // Remove leading table characters
              .replace(/[|\-\s]+\s*$/, '') // Remove trailing table characters
              .trim();

            if (cleanSentence.length > 30) { // Double-check length after cleaning
              index.push({
                id: item.id,
                title: item.content.title || item.title,
                breadcrumb: currentBreadcrumb.join(' > '),
                content: cleanSentence,
                type: 'content',
                fullContent: textContent
              });
            }
          });
        }

        // Add title and description as searchable items
        index.push({
          id: item.id,
          title: item.content.title || item.title,
          breadcrumb: currentBreadcrumb.join(' > '),
          content: item.content.description || '',
          type: 'title',
          fullContent: item.content.description || ''
        });
      }

      // Process children recursively
      if (item.children) {
        item.children.forEach(child => processItem(child, currentBreadcrumb));
      }
    };

    documentationStructure.forEach(item => processItem(item));
    setSearchIndex(index);
  };

  // Perform search with highlighting
  const performSearch = (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);

    // Simulate async search (you can replace with actual search logic)
    setTimeout(() => {
      const queryLower = query.toLowerCase();
      const results = [];
      const seenIds = new Set();

      searchIndex.forEach((item, index) => {
        const titleMatch = item.title.toLowerCase().includes(queryLower);
        const contentMatch = item.content.toLowerCase().includes(queryLower);

        if (titleMatch || contentMatch) {
          // Clean content for snippet extraction
          let cleanContent = item.content
            .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&') // Decode HTML entities
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();

          // Extract snippet with highlighted term
          let snippet = cleanContent;
          const matchIndex = cleanContent.toLowerCase().indexOf(queryLower);

          if (matchIndex !== -1) {
            const start = Math.max(0, matchIndex - 60);
            const end = Math.min(cleanContent.length, matchIndex + query.length + 60);
            snippet = cleanContent.substring(start, end);
            if (start > 0) snippet = '...' + snippet;
            if (end < cleanContent.length) snippet = snippet + '...';
          }

          // Ensure snippet is not too long and doesn't contain markdown artifacts
          snippet = snippet
            .replace(/\s+/g, ' ') // Normalize whitespace
            .substring(0, 150); // Limit snippet length

          // Create unique key for this result
          const uniqueKey = `${item.id}-${index}`;

          results.push({
            ...item,
            snippet,
            uniqueKey,
            relevance: titleMatch ? 2 : 1 // Higher relevance for title matches
          });
        }
      });

      // Sort by relevance and limit results
      const sortedResults = results
        .sort((a, b) => b.relevance - a.relevance)
        .slice(0, 20); // Increased limit to show more results

      setSearchResults(sortedResults);
      setIsLoading(false);
    }, 200);
  };

  // Highlight search terms in text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className={classes.highlight}>
          {part}
        </span>
      ) : part
    );
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setIsOpen(value.length > 0);
    performSearch(value);

    // If user clears the field, clear highlighting
    if (value === '') {
      onNavigate(null, '');
    }
  };

  const handleResultClick = (result) => {
    setIsOpen(false);
    // Keep search term in field so user can see what they searched for
    // Pass both search term and the specific snippet to scroll to
    onNavigate(result.id, searchTerm, result.snippet);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setIsOpen(false);
    // Notify parent to clear highlighting
    onNavigate(null, '');
  };

  return (
    <Box className={classes.searchContainer}>
      <TextField
        ref={searchRef}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search documentation..."
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={() => searchTerm && setIsOpen(true)}
        className={classes.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearSearch}
                style={{ padding: '4px' }}
                title="Clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={isOpen}
        anchorEl={searchRef.current}
        placement="bottom-start"
        style={{ width: searchRef.current?.offsetWidth || 300, zIndex: 1300 }}
      >
        <Paper className={classes.searchResults}>
          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={20} />
            </Box>
          ) : searchResults.length > 0 ? (
            <List disablePadding>
              {(() => {
                // Group results by page but show individual results
                const grouped = searchResults.reduce((acc, result) => {
                  const pageKey = result.breadcrumb;
                  if (!acc[pageKey]) {
                    acc[pageKey] = [];
                  }
                  acc[pageKey].push(result);
                  return acc;
                }, {});

                return Object.entries(grouped).map(([breadcrumb, pageResults]) => (
                  <Fragment key={breadcrumb}>
                    {Object.keys(grouped).length > 1 && pageResults.length > 1 && (
                      <div className={classes.categoryHeader}>
                        {breadcrumb} ({pageResults.length} results)
                      </div>
                    )}
                    {pageResults.map((result, index) => (
                      <ListItem
                        key={result.uniqueKey || `${result.id}-${index}`}
                        className={classes.resultItem}
                        onClick={() => handleResultClick(result)}
                      >
                        <ListItemText
                          primary={
                            <Box>
                              <Typography className={classes.resultBreadcrumb}>
                                {result.breadcrumb}
                              </Typography>
                              <Typography className={classes.resultTitle}>
                                {highlightText(result.title, searchTerm)}
                              </Typography>
                              <Typography className={classes.resultSnippet}>
                                {highlightText(result.snippet, searchTerm)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </Fragment>
                ));
              })()}
            </List>
          ) : searchTerm.length > 1 ? (
            <Typography className={classes.noResults}>
              No results found for "{searchTerm}"
            </Typography>
          ) : null}
        </Paper>
      </Popper>
    </Box>
  );
};

export default GlobalSearch;
